import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Phone, Mail, X, Zap, Calendar, Clock, Car } from "lucide-react";

/**
 * Quick Pre-call modal — fires a pre-call SMS/email without the diary import.
 *
 * Used for walk-in / phone bookings where the advisor wants the customer to
 * receive the standard pre-call reminder immediately. Creates a minimal
 * scheduled job in the background (so the audit trail + tomorrow's
 * pre-call list stay accurate) and stamps `pre_call_sent_at` on success.
 */
export default function QuickPreCallModal({ open, onClose, onSent, defaultDate }) {
  const [form, setForm] = useState({
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    vehicle_reg: "",
    vehicle_make: "",
    vehicle_model: "",
    appointment_date: defaultDate || tomorrowIso(),
    appointment_time: "",
    work_description: "",
    job_type: "dropoff",
    channels: { sms: true, email: true },
  });
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleChannel = (c) => setForm((f) => ({ ...f, channels: { ...f.channels, [c]: !f.channels[c] } }));

  const send = async () => {
    if (!form.customer_name.trim()) return toast.error("Customer name is required");
    if (!form.customer_phone.trim() && !form.customer_email.trim()) return toast.error("Add a phone or email");
    const channels = Object.entries(form.channels).filter(([_, v]) => v).map(([k]) => k);
    if (channels.length === 0) return toast.error("Pick at least one channel");

    setBusy(true);
    try {
      const r = await api.post("/pre-calls/quick", {
        customer_name: form.customer_name.trim(),
        customer_phone: form.customer_phone.trim() || null,
        customer_email: form.customer_email.trim() || null,
        vehicle_reg: form.vehicle_reg.trim(),
        vehicle_make: form.vehicle_make.trim() || null,
        vehicle_model: form.vehicle_model.trim() || null,
        appointment_date: form.appointment_date || null,
        appointment_time: form.appointment_time.trim() || null,
        work_description: form.work_description.trim() || null,
        job_type: form.job_type,
        channels,
      });
      const log = r.data?.dispatch_log || [];
      const sent = log.filter((l) => l.ok).map((l) => l.channel.toUpperCase());
      const failed = log.filter((l) => !l.ok).map((l) => l.channel.toUpperCase());
      if (sent.length) {
        toast.success(`Pre-call sent — ${sent.join(" + ")}${failed.length ? ` (${failed.join(", ")} failed)` : ""}`);
        onSent?.(r.data);
        onClose();
      } else {
        toast.error(`Nothing went through — ${failed.join(", ")} failed`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send pre-call");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-foreground/60 backdrop-blur-sm grid place-items-center px-4 py-8 overflow-y-auto"
      onClick={() => !busy && onClose()}
      data-testid="quick-precall-modal"
    >
      <div
        className="bg-background border border-border rounded-xl shadow-2xl w-full max-w-lg p-6 my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="label-overline text-primary flex items-center gap-1">
              <Zap className="w-3 h-3" /> Quick pre-call
            </div>
            <div className="font-display font-bold text-xl mt-1">Send without the diary</div>
            <p className="text-xs text-muted-foreground mt-1">For walk-ins / phone bookings. Creates a minimal job so it shows up on tomorrow&apos;s pre-call list.</p>
          </div>
          <button onClick={onClose} disabled={busy} className="text-muted-foreground hover:text-foreground" aria-label="Close" data-testid="quick-precall-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mt-5 space-y-3">
          <Field label="Customer name *" testid="qpc-name">
            <input value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} placeholder="Mr Chris Obi" className="input-default" data-testid="qpc-name-input" />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label={<><Phone className="inline w-3 h-3 mr-1" />Mobile (SMS)</>}>
              <input type="tel" value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} placeholder="+44 7700 900000" className="input-default" data-testid="qpc-phone-input" />
            </Field>
            <Field label={<><Mail className="inline w-3 h-3 mr-1" />Email</>}>
              <input type="email" value={form.customer_email} onChange={(e) => set("customer_email", e.target.value)} placeholder="customer@example.com" className="input-default" data-testid="qpc-email-input" />
            </Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label={<><Car className="inline w-3 h-3 mr-1" />Reg *</>}>
              <input value={form.vehicle_reg} onChange={(e) => set("vehicle_reg", e.target.value.toUpperCase())} placeholder="AB12 CDE" className="input-default uppercase" data-testid="qpc-reg-input" />
            </Field>
            <Field label="Make">
              <input value={form.vehicle_make} onChange={(e) => set("vehicle_make", e.target.value)} placeholder="Toyota" className="input-default" data-testid="qpc-make-input" />
            </Field>
            <Field label="Model">
              <input value={form.vehicle_model} onChange={(e) => set("vehicle_model", e.target.value)} placeholder="RAV4" className="input-default" data-testid="qpc-model-input" />
            </Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label={<><Calendar className="inline w-3 h-3 mr-1" />Appointment date</>}>
              <input type="date" value={form.appointment_date} onChange={(e) => set("appointment_date", e.target.value)} className="input-default" data-testid="qpc-date-input" />
            </Field>
            <Field label={<><Clock className="inline w-3 h-3 mr-1" />Time (e.g. 10:30)</>}>
              <input type="text" value={form.appointment_time} onChange={(e) => set("appointment_time", e.target.value)} placeholder="10:30" className="input-default" data-testid="qpc-time-input" />
            </Field>
          </div>

          <Field label="Work description">
            <input value={form.work_description} onChange={(e) => set("work_description", e.target.value)} placeholder="Full service + MOT" className="input-default" data-testid="qpc-work-input" />
          </Field>

          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Send via</div>
            <div className="flex flex-wrap gap-2 mt-2">
              <ChannelToggle label="SMS" icon={<Phone className="w-4 h-4" />} active={form.channels.sms} onClick={() => toggleChannel("sms")} testid="qpc-channel-sms" />
              <ChannelToggle label="Email" icon={<Mail className="w-4 h-4" />} active={form.channels.email} onClick={() => toggleChannel("email")} testid="qpc-channel-email" />
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <button onClick={onClose} disabled={busy} className="btn-outline" data-testid="qpc-cancel-btn">Cancel</button>
          <button onClick={send} disabled={busy} className="btn-primary" data-testid="qpc-send-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
            Send pre-call now
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function ChannelToggle({ label, icon, active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition " +
        (active ? "bg-primary text-primary-foreground border-primary" : "bg-background text-foreground border-border hover:border-primary/40")
      }
      data-testid={testid}
      data-active={active}
    >
      {icon}
      {label}
    </button>
  );
}

function tomorrowIso() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}
